import React, { useState, useEffect } from 'react';
import api from '../api';

export default function Costs() {
  const [costs, setCosts] = useState([]);
  const [clients, setClients] = useState([]);
  const [workOrders, setWorkOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ category:'', client_id:'', work_order_id:'', planned:'', actual:'', notes:'', period_month:'' });
  const [saving, setSaving] = useState(false);
  const [totals, setTotals] = useState({ planned:0, actual:0, pct:0 });
  const [clientFilter, setClientFilter] = useState('');

  const CATEGORIES = ['Content Creation','Paid Advertising','Design','Video Production','Influencer','Tools & Software','Miscellaneous'];

  useEffect(() => { loadData(); }, [clientFilter]); // eslint-disable-line

  const loadData = async () => {
    try {
      const [cRes, clRes, woRes] = await Promise.all([
        api.get(`/costs${clientFilter ? `?client_id=${clientFilter}` : ''}`),
        api.get('/clients'),
        api.get('/work-orders'),
      ]);
      setCosts(cRes.data.data);
      setTotals({ planned: cRes.data.total_planned, actual: cRes.data.total_actual, pct: cRes.data.budget_pct });
      setClients(clRes.data.data);
      setWorkOrders(woRes.data.data);
    } catch {} finally { setLoading(false); }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post('/costs', { ...form, planned: parseFloat(form.planned)||0, actual: parseFloat(form.actual)||0, client_id: form.client_id||null, work_order_id: form.work_order_id||null });
      setShowForm(false);
      setForm({ category:'', client_id:'', work_order_id:'', planned:'', actual:'', notes:'', period_month:'' });
      loadData();
    } catch {} finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this cost entry?')) return;
    await api.delete(`/costs/${id}`);
    loadData();
  };

  if (loading) return <div className="page-wrap"><div className="empty-state"><div className="empty-state-title">Loading...</div></div></div>;

  return (
    <div className="page-wrap">
      <div style={{ display:'flex', justifyContent:'flex-end', gap:'8px', marginBottom: 16 }}>
        <select className="form-select" style={{ width: '160px' }} value={clientFilter} onChange={e => setClientFilter(e.target.value)}>
          <option value="">All Clients</option>
          {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <button className="btn btn-primary" onClick={() => setShowForm(true)}>+ Add Cost</button>
      </div>

      {/* Summary KPI cards */}
      <div className="grid-4 mb-16">
        <div className="kpi-card blue">
          <div className="kpi-label">Total Planned</div>
          <div className="kpi-value" style={{ fontSize: '1.4rem' }}>₹{Number(totals.planned).toLocaleString('en-IN')}</div>
        </div>
        <div className="kpi-card yellow">
          <div className="kpi-label">Total Actual</div>
          <div className="kpi-value" style={{ fontSize: '1.4rem', color: totals.actual > totals.planned ? 'var(--red)' : 'var(--green)' }}>₹{Number(totals.actual).toLocaleString('en-IN')}</div>
        </div>
        <div className="kpi-card purple">
          <div className="kpi-label">Budget Used</div>
          <div className="kpi-value" style={{ fontSize: '1.4rem', color: totals.pct > 100 ? 'var(--red)' : totals.pct > 80 ? 'var(--yellow)' : 'var(--green)' }}>{totals.pct}%</div>
          <div className="progress-bar" style={{ marginTop: '8px' }}>
            <div className="progress-fill" style={{ width: `${Math.min(100, totals.pct)}%`, background: totals.pct > 100 ? 'var(--red)' : totals.pct > 80 ? 'var(--yellow)' : 'var(--green)' }} />
          </div>
        </div>
        <div className="kpi-card green">
          <div className="kpi-label">Variance</div>
          <div className="kpi-value" style={{ fontSize: '1.4rem', color: totals.actual > totals.planned ? 'var(--red)' : 'var(--green)' }}>
            {totals.actual > totals.planned ? '▲' : '▼'} ₹{Math.abs(totals.actual - totals.planned).toLocaleString('en-IN')}
          </div>
        </div>
      </div>

      {costs.length === 0 ? (
        <div className="empty-state card">
          <div className="empty-state-icon">💰</div>
          <div className="empty-state-title">No cost entries yet</div>
          <div className="text-sm text-muted">Click "+ Add Cost" to track your first budget item.</div>
        </div>
      ) : (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Category</th>
                <th>Client</th>
                <th>Work Order</th>
                <th>Planned (₹)</th>
                <th>Actual (₹)</th>
                <th>Variance</th>
                <th>Period</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {costs.map(c => {
                const v = (c.actual||0) - (c.planned||0);
                return (
                  <tr key={c.id}>
                    <td><span className="fw-600">{c.category}</span></td>
                    <td className="text-muted">{c.client?.name || '—'}</td>
                    <td className="text-muted">{c.work_order?.wo_number || '—'}</td>
                    <td><span className="mono">₹{Number(c.planned).toLocaleString('en-IN')}</span></td>
                    <td><span className="mono">₹{Number(c.actual||0).toLocaleString('en-IN')}</span></td>
                    <td><span className="mono fw-700" style={{ color: v > 0 ? 'var(--red)' : 'var(--green)' }}>{v > 0 ? '▲' : '▼'} ₹{Math.abs(v).toLocaleString('en-IN')}</span></td>
                    <td className="text-muted mono">{c.period_month || '—'}</td>
                    <td><button className="icon-btn" onClick={() => del(c.id)}>✕</button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <div className="modal-overlay">
          <div className="modal-box">
            <div className="modal-header">
              <div className="modal-title">+ Add Cost Entry</div>
              <button className="modal-close" onClick={() => setShowForm(false)}>✕</button>
            </div>
            <div className="modal-body">
              <form onSubmit={save}>
                <div className="form-group"><label className="form-label">Category *</label>
                  <select className="form-select" value={form.category} onChange={e => setForm({...form, category: e.target.value})}>
                    <option value="">Select...</option>
                    {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div className="form-row">
                  <div className="form-group"><label className="form-label">Client</label>
                    <select className="form-select" value={form.client_id} onChange={e => setForm({...form, client_id: e.target.value})}>
                      <option value="">All clients</option>
                      {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                  <div className="form-group"><label className="form-label">Work Order</label>
                    <select className="form-select" value={form.work_order_id} onChange={e => setForm({...form, work_order_id: e.target.value})}>
                      <option value="">None</option>
                      {workOrders.map(w => <option key={w.id} value={w.id}>{w.wo_number} — {w.title}</option>)}
                    </select>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group"><label className="form-label">Planned (₹) *</label><input className="form-input" type="number" value={form.planned} onChange={e => setForm({...form, planned: e.target.value})} /></div>
                  <div className="form-group"><label className="form-label">Actual (₹)</label><input className="form-input" type="number" value={form.actual} onChange={e => setForm({...form, actual: e.target.value})} /></div>
                </div>
                <div className="form-row">
                  <div className="form-group"><label className="form-label">Period Month</label><input className="form-input" type="month" value={form.period_month} onChange={e => setForm({...form, period_month: e.target.value})} /></div>
                  <div className="form-group"><label className="form-label">Notes</label><input className="form-input" value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} /></div>
                </div>
                <div className="modal-footer" style={{ padding: '0', border: 'none', marginTop: '8px' }}>
                  <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
                  <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving...' : 'Add Cost'}</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
